import { useState } from "react";
import {
  settingsStore,
  setAgentMode,
  installAgentUpdate,
  checkAgentUpdates,
} from "../../stores/settingsStore";
import type { AgentUpdateEntry } from "../../api/client";

const AGENT_LABELS: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  cursor: "Cursor",
  gemini: "Gemini CLI",
  copilot: "GitHub Copilot",
  grok: "Grok",
  opencode: "opencode",
};
const AGENT_ORDER = ["claude", "codex", "cursor", "gemini", "copilot", "grok", "opencode"];

export function AgentsPanel() {
  const state = settingsStore.useStore();
  const [savedAgent, setSavedAgent] = useState<string | null>(null);
  const [busyAgent, setBusyAgent] = useState<string | null>(null);
  const [busyCheck, setBusyCheck] = useState(false);
  const [logsOpen, setLogsOpen] = useState<Record<string, boolean>>({});

  async function onModeChange(agentId: string, modeId: string) {
    await setAgentMode(agentId, modeId);
    setSavedAgent(agentId);
    setTimeout(() => setSavedAgent((cur) => (cur === agentId ? null : cur)), 1000);
  }

  async function onUpdate(agentId: string) {
    setBusyAgent(agentId);
    try {
      await installAgentUpdate(agentId);
    } catch {
      // Failure is already rendered via the snapshot status (set on stream end via loadAgentUpdates).
      // Logs are visible in the <details>.
    } finally {
      setBusyAgent((cur) => (cur === agentId ? null : cur));
    }
  }

  async function onCheck() {
    setBusyCheck(true);
    try {
      await checkAgentUpdates(true);
    } finally {
      setBusyCheck(false);
    }
  }

  async function onUpdateAll() {
    const ids = AGENT_ORDER.filter((id) => state.updates?.snapshot?.[id]?.status === "available");
    for (const id of ids) {
      setBusyAgent(id);
      try {
        await installAgentUpdate(id);
      } catch {
        // Per spec: stop on first failure. Leave subsequent agents pending.
        setBusyAgent(null);
        return;
      }
    }
    setBusyAgent(null);
  }

  return (
    <div className="settings-panel">
      <h3>Agents</h3>
      <p className="settings-help">Choose the startup mode each agent boots into when spawned.</p>

      <div className="agents-panel__controls">
        <button
          type="button"
          className="agents-panel__check-btn"
          onClick={() => void onCheck()}
          disabled={busyCheck}
        >
          Check for updates
        </button>
        {Object.values(state.updates?.snapshot ?? {}).some((e) => e.status === "available") && (
          <button
            type="button"
            className="agents-panel__update-all-btn"
            onClick={() => void onUpdateAll()}
            disabled={busyCheck || !!busyAgent}
          >
            Update all
          </button>
        )}
      </div>

      {AGENT_ORDER.map((id) => {
        const modes = state.agentModes[id] || [];
        if (!modes.length) return null;
        const current = state.settings.agents[id] || modes[0].id;
        const selectedMode = modes.find((m) => m.id === current);
        const label = AGENT_LABELS[id] || id;
        const entry: AgentUpdateEntry | undefined = state.updates?.snapshot?.[id];
        const logs = state.installing?.[id] || [];
        const open = logsOpen[id] || false;

        return (
          <div key={id} className="agents-row">
            <label className="agents-row__label" htmlFor={`agent-${id}`}>
              {label}
            </label>
            <select
              id={`agent-${id}`}
              className="agents-row__select"
              value={current}
              onChange={(e) => void onModeChange(id, e.target.value)}
            >
              {modes.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.unsafe ? "⚠ " : ""}
                  {m.label}
                </option>
              ))}
            </select>
            {selectedMode && (
              <p className={`agents-row__desc${selectedMode.unsafe ? " is-unsafe" : ""}`}>
                {selectedMode.description}
              </p>
            )}
            {savedAgent === id && <span className="agents-row__saved">Saved</span>}

            <div className="agents-row__update">
              <UpdateRow
                id={id}
                label={label}
                entry={entry}
                busy={busyAgent === id}
                result={state.installResult?.[id]}
                hasLogs={logs.length > 0}
                onUpdate={() => void onUpdate(id)}
                onRetry={() => void checkAgentUpdates(true)}
                logsOpen={open}
                onToggleLogs={() => setLogsOpen((m) => ({ ...m, [id]: !m[id] }))}
                disableUpdate={busyCheck}
              />
              {logs.length > 0 && open && (
                <pre className="agents-row__logs" aria-label={`${label} update logs`}>
                  {logs
                    .filter((e) => e.type === "log")
                    .map((e) => (e as any).chunk)
                    .join("")}
                </pre>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UpdateRow({
  id,
  label,
  entry,
  busy,
  result,
  hasLogs,
  onUpdate,
  onRetry,
  logsOpen,
  onToggleLogs,
  disableUpdate = false,
}: {
  id: string;
  label: string;
  entry: AgentUpdateEntry | undefined;
  busy: boolean;
  result: { state: "success" | "failure"; installed: string | null } | undefined;
  hasLogs: boolean;
  onUpdate: () => void;
  onRetry: () => void;
  logsOpen: boolean;
  onToggleLogs: () => void;
  disableUpdate?: boolean;
}) {
  const logsToggle = hasLogs ? (
    <button type="button" className="agents-row__logs-toggle" onClick={onToggleLogs}>
      {logsOpen ? "Hide logs ▴" : "Show logs ▾"}
    </button>
  ) : null;

  if (busy) {
    return <span className="agents-row__update-status">Updating… {logsToggle}</span>;
  }
  // Post-install result takes precedence over entry status until cleared on next install.
  if (result?.state === "success") {
    return (
      <span className="agents-row__update-status">
        Updated to v{result.installed} ✓ {logsToggle}
      </span>
    );
  }
  if (result?.state === "failure") {
    return <span className="agents-row__update-status">Failed {logsToggle}</span>;
  }
  if (!entry) return <span className="agents-row__update-status">—</span>;

  if (entry.status === "up-to-date") {
    return <span className="agents-row__update-status">v{entry.installed} — up to date</span>;
  }
  if (entry.status === "available") {
    return (
      <span className="agents-row__update-status">
        v{entry.installed} → v{entry.latest} available{" "}
        <button
          type="button"
          className="agents-row__update-btn"
          aria-label={`Update ${label}`}
          onClick={onUpdate}
          disabled={disableUpdate}
        >
          Update
        </button>
      </span>
    );
  }
  if (entry.status === "not-installed") {
    // Same SSE install route as Update — the host picks the driver's
    // fresh-install channel (brew on macOS, npm elsewhere) when the
    // snapshot says the CLI is missing.
    return (
      <span className="agents-row__update-status">
        not installed{" "}
        <button
          type="button"
          className="agents-row__update-btn"
          aria-label={`Install ${label}`}
          onClick={onUpdate}
          disabled={disableUpdate}
        >
          Install
        </button>
      </span>
    );
  }
  // error
  return (
    <span className="agents-row__update-status">
      (couldn't check){" "}
      <button type="button" className="agents-row__update-btn" onClick={onRetry}>
        Retry
      </button>
    </span>
  );
}
